import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { RichAircraft } from '../data/enrich';
import { isEmergency } from '../map/aircraftIcons';
import { inferFlightPathPitch } from '../map/aircraftAltitude';
import { MODEL_YAW_OFFSET_DEG } from '../map/aircraftModelLayer';
import { allModelUrls, getAircraftModelDef } from '../map/aircraftModels';
import { arWingspanM, effectiveArWingspanM, slantRangeM } from './aircraftArScale';
import type { DevicePose, ProjectedAircraft } from './geometry';

interface LoadedModel {
  url: string;
  scene: THREE.Group;
  span: number;
}

export interface SkyModelFrame {
  pose: DevicePose;
  fovXDeg: number;
  fovYDeg: number;
  projected: ProjectedAircraft[];
  aircraftByIcao: Map<string, RichAircraft>;
}

interface InstanceRecord {
  obj: THREE.Object3D;
  kindKey: string;
}

function tintMaterial(obj: THREE.Object3D, colorHex: string): void {
  const color = new THREE.Color(colorHex);
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      const m = mat as THREE.MeshStandardMaterial;
      if (m.isMeshStandardMaterial) {
        m.color = color;
        m.depthTest = true;
        m.depthWrite = true;
        m.side = THREE.DoubleSide;
        m.transparent = true;
        m.opacity = 0.92;
        m.needsUpdate = true;
      }
    }
  });
}

function disableFrustumCulling(obj: THREE.Object3D): void {
  obj.frustumCulled = false;
  obj.traverse((child) => {
    child.frustumCulled = false;
  });
}

function buildInstance(loaded: LoadedModel): THREE.Object3D {
  const obj = loaded.scene.clone(true);
  disableFrustumCulling(obj);
  obj.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of mats) {
      const m = mat as THREE.MeshStandardMaterial;
      if (m.isMeshStandardMaterial) {
        m.depthTest = true;
        m.depthWrite = true;
        m.side = THREE.DoubleSide;
        m.transparent = true;
        m.opacity = 0.92;
      }
    }
  });
  return obj;
}

/** Place an aircraft in camera space using the same linear FOV model as HUD labels. */
function cameraPositionFromProjection(
  proj: ProjectedAircraft,
  fovXDeg: number,
  fovYDeg: number,
  out: THREE.Vector3,
): THREE.Vector3 {
  const dAz = (proj.x - 0.5) * fovXDeg;
  const dEl = (0.5 - proj.y) * fovYDeg;
  const range = slantRangeM(proj.distanceNm, proj.elevationDeg);
  const dAzRad = (dAz * Math.PI) / 180;
  const dElRad = (dEl * Math.PI) / 180;
  out.set(Math.tan(dAzRad) * range, Math.tan(dElRad) * range, -range);
  return out;
}

function orientInstance(
  obj: THREE.Object3D,
  trackDeg: number | null,
  pitchDeg: number,
  cameraAzimuth: number,
): void {
  const relTrack = (trackDeg ?? 0) - cameraAzimuth;
  const yawDeg = relTrack + MODEL_YAW_OFFSET_DEG;
  obj.rotation.order = 'ZXY';
  obj.rotation.x = THREE.MathUtils.degToRad(90 + pitchDeg);
  obj.rotation.y = 0;
  obj.rotation.z = -THREE.MathUtils.degToRad(yawDeg);
}

export class SkyModelRenderer {
  private canvas: HTMLCanvasElement;
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private loader = new GLTFLoader();
  private models = new Map<string, LoadedModel>();
  private instances = new Map<string, InstanceRecord>();
  private posScratch = new THREE.Vector3();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(48, 1, 0.5, 500_000);
    this.camera.position.set(0, 0, 0);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);

    const ambient = new THREE.AmbientLight(0xffffff, 0.75);
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(0.3, 0.8, 0.5).normalize();
    const fill = new THREE.DirectionalLight(0x93c5fd, 0.45);
    fill.position.set(-0.6, 0.2, 0.4).normalize();
    this.scene.add(ambient, key, fill);

    for (const url of allModelUrls()) void this.loadModel(url);
    this.resize(canvas.clientWidth, canvas.clientHeight);
  }

  resize(width: number, height: number): void {
    if (width <= 0 || height <= 0) return;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  dispose(): void {
    for (const { obj } of this.instances.values()) {
      this.scene.remove(obj);
      obj.traverse((child) => {
        const mesh = child as THREE.Mesh;
        if (!mesh.isMesh) return;
        mesh.geometry?.dispose();
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const mat of mats) mat.dispose();
      });
    }
    this.instances.clear();
    this.renderer.dispose();
  }

  update(frame: SkyModelFrame): void {
    const { pose, fovXDeg, fovYDeg, projected, aircraftByIcao } = frame;
    this.camera.fov = fovYDeg;
    this.camera.aspect = this.canvas.clientWidth / Math.max(this.canvas.clientHeight, 1);
    this.camera.updateProjectionMatrix();

    const active = new Set<string>();
    const heightPx = this.canvas.clientHeight;

    for (const proj of projected) {
      if (!proj.inView) continue;
      const ac = aircraftByIcao.get(proj.icao);
      if (!ac || ac.onGround) continue;
      if (ac.lat == null || ac.lon == null) continue;
      if ((ac.altBaro ?? 0) <= 0) continue;

      const emergency = isEmergency(ac);
      const def = getAircraftModelDef(ac.iconKind, emergency);
      const loaded = this.models.get(def.url);
      if (!loaded) continue;

      active.add(ac.icao);
      let record = this.instances.get(ac.icao);
      if (!record) {
        const obj = buildInstance(loaded);
        obj.userData.icao = ac.icao;
        this.scene.add(obj);
        record = { obj, kindKey: '' };
        this.instances.set(ac.icao, record);
      }

      const { obj } = record;
      const kindKey = `${ac.iconKind}|${emergency}|${def.preserveColor}`;
      if (record.kindKey !== kindKey) {
        if (!def.preserveColor) tintMaterial(obj, def.color);
        record.kindKey = kindKey;
      }

      const realSpan = arWingspanM(ac.iconKind);
      const range = slantRangeM(proj.distanceNm, proj.elevationDeg);
      const wingspan = effectiveArWingspanM(realSpan, range, fovYDeg, heightPx);
      const scale = wingspan / loaded.span;
      obj.scale.setScalar(scale);

      cameraPositionFromProjection(proj, fovXDeg, fovYDeg, this.posScratch);
      obj.position.copy(this.posScratch);

      const pitchDeg = inferFlightPathPitch(ac.verticalRate, ac.groundSpeed);
      orientInstance(obj, ac.track, pitchDeg, pose.azimuth);
      obj.visible = true;
    }

    for (const [icao, record] of this.instances) {
      if (!active.has(icao)) record.obj.visible = false;
    }

    this.renderer.render(this.scene, this.camera);
  }

  private async loadModel(url: string): Promise<void> {
    if (this.models.has(url)) return;
    try {
      const gltf = await this.loader.loadAsync(url);
      const scene = gltf.scene;
      const box = new THREE.Box3().setFromObject(scene);
      const span = Math.max(box.max.x - box.min.x, 0.0001);
      this.models.set(url, { url, scene, span });
    } catch {
      // GLB may be missing in dev until build:gen runs — skip silently.
    }
  }
}
